import React from 'react';

import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { Calories } from 'components/price-and-calories';
import { Box } from 'components/ui';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ProductPriceConditions, useProductPrice } from 'hooks/menu/use-product-price';
import { computeProductCalories } from 'hooks/menu/utils';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';

import { PriceAndCaloriesWrapper, PriceCaloriesText } from './styled';

interface IPriceAndCaloriesProps {
  productPriceConditions?: ProductPriceConditions;
  icon?: React.ReactNode;
  fontColorOverride?: string;
}

export const PriceAndCalories: React.FC<IPriceAndCaloriesProps> = ({
  productPriceConditions,
  icon,
  fontColorOverride,
}) => {
  const { region } = useLocale();
  const { formattedProductPrice } = useProductPrice(productPriceConditions);

  const { selectedProduct } = useProductWizardContext();
  const { formatCalories } = useMenuCalories();

  const shouldComputeCalories = !!selectedProduct && !selectedProduct.hideCalories;
  const productCalories = shouldComputeCalories ? computeProductCalories(selectedProduct) : 0;
  const formattedProductCalories = formatCalories(productCalories);

  const priceAccessibilityLabel = t('totalPrice', { price: formattedProductPrice });

  const enableSodiumWarning = useFlag(LaunchDarklyFlag.ENABLE_SODIUM_WARNING);
  const shouldDisplaySodiumWarning = enableSodiumWarning && !!icon && region === Region.US;

  return (
    <Box style={styles.priceAndCaloriesContainer}>
      <PriceAndCaloriesWrapper>
        <PriceCaloriesText
          testID="product-total-price"
          color={fontColorOverride}
          accessibilityLabel={priceAccessibilityLabel}
          accessible
        >
          {formattedProductPrice}
        </PriceCaloriesText>

        {!!formattedProductPrice &&
          !!formattedProductCalories &&
          !selectedProduct?.hideCalories && (
            <PriceCaloriesText color={fontColorOverride}>{' | '}</PriceCaloriesText>
          )}

        <Calories hideCalories={!!selectedProduct?.hideCalories}>
          <PriceCaloriesText
            testID="product-total-calories"
            accessible
            color={fontColorOverride}
            accessibilityLabel={formattedProductCalories}
          >
            {formattedProductCalories}
          </PriceCaloriesText>
        </Calories>
      </PriceAndCaloriesWrapper>

      {shouldDisplaySodiumWarning && (
        <View style={styles.container}>
          {icon}
          <Typography fontSize={18}>
            {t('sodiumWarning', 'Sodium Warning').toUpperCase()}
          </Typography>
        </View>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 4,
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.$2,
  },
  priceAndCaloriesContainer: {
    alignItems: 'center',
  },
});
